/**
 * @voltxampere/web — Command palette UI state (Sprint 22 C2)
 *
 * Pure Preact Signals; no DOM or Lit coupling. The palette
 * component (Sprint 22 C2) and the shortcut catalog
 * (`shortcuts/catalog.ts`) write to these signals, the component
 * subscribes via `SignalController`.
 *
 * `recentCommandIds` is bootstrapped to an empty list — Sprint 22
 * C4 wires the IDB-backed recent store into this signal so the
 * palette empty-state renders the user's history. Until C4 lands,
 * the signal stays empty and only the Suggested section appears.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";

/** Free-text query bound to the palette search input. */
export const paletteQuery = signal<string>("");

/**
 * Index of the currently highlighted result row. Bounded to
 * `[0, results.length - 1]` by the component each time the
 * results list re-renders. A standalone signal (rather than a
 * derived computed) keeps the keyboard-nav reducer simple.
 */
export const selectedCommandIndex = signal<number>(0);

/**
 * Id of the command currently being awaited. `null` when idle.
 * The palette UI shows a spinner overlay on the matching row
 * while a long-running command (export, IDB read, etc.) resolves;
 * Cmd+K reopens during the await are debounced by checking this
 * flag.
 */
export const paletteExecuting = signal<string | null>(null);

/**
 * Most recently executed commands, oldest → newest at the tail.
 * Sprint 22 C2 reads this signal to render the empty-state
 * Recent section. Sprint 22 C4 owns writes (IDB hydration +
 * post-execute persistence). C2 ships a stub `recordRecent`
 * helper so the palette can demonstrate the section in DEV
 * without C4 — production wiring overrides the stub.
 */
export const recentCommandIds = signal<readonly string[]>([]);

const MAX_RECENT = 8;

/**
 * In-memory recent recorder. Sprint 22 C4 will replace this with
 * an IDB-backed implementation that survives reloads. Until then
 * the palette uses this stub so the UI rewrite (C2) can be
 * validated end-to-end without crossing the persistence boundary.
 */
export function recordRecent(id: string): void {
  const previous = recentCommandIds.value;
  const without = previous.filter((existing) => existing !== id);
  // Newest at the FRONT so the empty-state list reads top-down
  // chronologically (most-recent first).
  const next = [id, ...without].slice(0, MAX_RECENT);
  recentCommandIds.value = next;
}

/** Reset every palette signal to its idle state. */
export function resetPaletteState(): void {
  paletteQuery.value = "";
  selectedCommandIndex.value = 0;
  paletteExecuting.value = null;
}
